import { afterEveryRender, AfterRenderRef, Component, Inject, input, OnChanges, OnDestroy, SimpleChanges, ElementRef, HostListener, ViewChild } from '@angular/core';
import {Observable} from "rxjs";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {
  RdsFormFieldModule,
  RdsMultiAutocompleteComponent,
} from "@rds/angular-components";
import {DOCUMENT} from "@angular/common";
import {AutocompletionOptionsService} from "../../services/autocompletion-options.service";
import {Study} from "../../models/metadata";

@Component({
  selector: 'app-dynamic-filter-autocompletion',
  standalone: true,
  imports: [
    RdsFormFieldModule,
    RdsMultiAutocompleteComponent,
    ReactiveFormsModule
  ],
  templateUrl: './dynamic-filter-autocompletion.component.html',
  styleUrl: './dynamic-filter-autocompletion.component.scss'
})
export class DynamicFilterAutocompletionComponent<T = Study | string> implements OnDestroy, OnChanges{
  @ViewChild(RdsMultiAutocompleteComponent) multiAutocomplete!: RdsMultiAutocompleteComponent<T>;
  
  elementID = input.required<string>()
  type = input.required<'studies' | 'persons' | 'targets' | 'strategic-pillars' | 'audiences'>()
  control = input.required<FormControl<Array<T> | null>>()
  label = input<string>()
  staticOptions = input<T[]>([])  // For strategic pillars and audiences
  options: ((query: string) => Observable<T[]>) | T[] = []
  readonly defaultFormatter = (item: T) => item as unknown as string
  displayFormatter: (item: T) => string = this.defaultFormatter

  acChipsObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes?.forEach(node => {
        const chip = node as HTMLElement
        if(chip.classList && chip.classList.contains('rds-chips__chip')) {
          chip.setAttribute('style', 'display: none !important')
        }
      })
    });
  });

  acInputContainerObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const ele = (mutation.target as Element)
      if(mutation.attributeName === 'style' && ele.getAttribute('style') !== ""){
        ele.setAttribute('style', '')
      }
    });
  });

  constructor(@Inject(DOCUMENT) private document: Document, private autocompleteOptionsService: AutocompletionOptionsService, private elementRef: ElementRef) {
    let renderRef: AfterRenderRef
    renderRef = afterEveryRender({ earlyRead: () => {
        const multiContainer = this.getACElement('.rds-autocomplete-multi-container');
        if (multiContainer) {
            // Hide any existing chips before observing
            multiContainer.querySelectorAll('.rds-chips__chip').forEach(chip => {
                (chip as HTMLElement).setAttribute('style', 'display: none !important');
            });
            
            this.acChipsObserver.observe(multiContainer, {
                attributes: false,
                childList: true,
                characterData: false,
                subtree: true  // Observe deeper to catch chips added at any level
            });
        }
        const inputContainer = this.getACElement('.rds-autocomplete-input-container');
        if (inputContainer) {
            this.acInputContainerObserver.observe(inputContainer, {
                attributes: true,
                childList: false,
                characterData: false
            });
            
            // Set initial placeholder if there are selections
            const currentValue = this.control().value;
            if (currentValue && currentValue.length > 0) {
                const input = inputContainer.querySelector('input');
                if (input) {
                    input.setAttribute('placeholder', currentValue.length + ' selected');
                }
            }
        }
        renderRef.destroy();
    } }, )
  }

  private adaptToType(){
    switch(this.type()){
      case 'studies':
        // @ts-ignore
        this.options = this.autocompleteOptionsService.getStudies.bind(this.autocompleteOptionsService)
        // @ts-ignore
        this.displayFormatter = (item: Study) => `${item.studyNumber} ${item.studyName? item.studyName : ''}`
        break;
      case 'persons':
        // @ts-ignore
        this.options = this.autocompleteOptionsService.getPersons.bind(this.autocompleteOptionsService)
        this.displayFormatter = this.defaultFormatter
        break;
      case 'targets':
        // @ts-ignore
        this.options = this.autocompleteOptionsService.getTargets.bind(this.autocompleteOptionsService)
        this.displayFormatter = this.defaultFormatter
        break;
      case 'strategic-pillars':
      case 'audiences':
        // Use static options passed via input
        this.options = this.staticOptions()
        // @ts-ignore
        this.displayFormatter = (item: any) => item.title || item
        break;
      default:
        this.options = []
        this.displayFormatter = this.defaultFormatter
        break;

    }
  }

  ngOnDestroy(): void {
    this.acChipsObserver.disconnect()
    this.acInputContainerObserver.disconnect()
  }

  getACElement(query: string){
    return this.document.querySelector(`#${this.elementID()} ${query}`)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if(changes["type"] || changes["staticOptions"]) {
      this.adaptToType()
    }
    if(changes['control']){
      this.control().valueChanges.subscribe(selections => {
        if(selections && selections.length > 0){
          const container = this.getACElement('.rds-autocomplete-input-container')!
          const input = container?.querySelector('input');
          if (input) {
            input.setAttribute('placeholder', selections.length + ' selected');
          }
          
          // Also hide any chips that might be visible
          const multiContainer = this.getACElement('.rds-autocomplete-multi-container');
          multiContainer?.querySelectorAll('.rds-chips__chip').forEach(chip => {
            (chip as HTMLElement).setAttribute('style', 'display: none !important');
          });
        }
      })
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Check if click is inside this component's autocomplete or overlay
    const clickedInsideComponent = this.elementRef.nativeElement.contains(target);
    const clickedInsideOverlay = target.closest('.rds-autocomplete-panel') || 
                                  target.closest('.cdk-overlay-container');
    if (!clickedInsideComponent && !clickedInsideOverlay) {
      this.closeDropdown();
    }
  }

  closeDropdown() {
    this.multiAutocomplete?.hide();
  }
}
